import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Star, ChevronRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const SPANISH_REGIONS = [
  {
    name: "Área de Sacramento",
    regionSlug: "sacramento-area",
    hubPath: "/es/sacramento",
    color: "from-blue-500/20 to-primary/10",
    cities: [
      { name: "Sacramento", path: "/es/sacramento" },
      { name: "Roseville", path: "/es/roseville" },
      { name: "Folsom", path: "/es/folsom" },
      { name: "Elk Grove", path: "/es/elk-grove" },
      { name: "Davis", path: "/es/davis" },
      { name: "Rocklin", path: "/es/rocklin" },
      { name: "Granite Bay", path: "/es/granite-bay" },
      { name: "El Dorado Hills", path: "/es/el-dorado-hills" },
      { name: "West Sacramento", path: "/es/west-sacramento" },
      { name: "Rancho Cordova", path: "/es/rancho-cordova" },
      { name: "Carmichael", path: "/es/carmichael" },
      { name: "Citrus Heights", path: "/es/citrus-heights" },
    ],
  },
  {
    name: "Condado de Los Ángeles",
    regionSlug: "los-angeles",
    hubPath: "/es/los-angeles",
    color: "from-purple-500/20 to-pink-500/10",
    cities: [
      { name: "Los Ángeles (Hub)", path: "/es/los-angeles" },
      { name: "Westside LA", path: "/es/west-la" },
      { name: "San Fernando Valley", path: "/es/north-la" },
      { name: "San Gabriel Valley", path: "/es/east-la" },
      { name: "South Bay", path: "/es/south-la" },
      { name: "Central LA", path: "/es/central-la" },
      { name: "Pasadena", path: "/es/pasadena" },
      { name: "Long Beach", path: "/es/long-beach" },
      { name: "Santa Mónica", path: "/es/santa-monica" },
      { name: "Culver City", path: "/es/culver-city" },
      { name: "Manhattan Beach", path: "/es/manhattan-beach" },
      { name: "Encino", path: "/es/encino" },
    ],
  },
  {
    name: "Área de San Francisco",
    regionSlug: "san-francisco",
    hubPath: "/es/san-francisco",
    color: "from-orange-500/20 to-yellow-500/10",
    cities: [
      { name: "San Francisco", path: "/es/san-francisco" },
      { name: "Oakland", path: "/es/oakland" },
      { name: "Daly City", path: "/es/daly-city" },
      { name: "San Mateo", path: "/es/san-mateo" },
      { name: "Burlingame", path: "/es/burlingame" },
      { name: "South San Francisco", path: "/es/south-san-francisco" },
    ],
  },
  {
    name: "Condado de Santa Clara",
    regionSlug: "santa-clara",
    hubPath: "/es/santa-clara",
    color: "from-green-500/20 to-teal-500/10",
    cities: [
      { name: "San José", path: "/es/san-jose" },
      { name: "San José South Bay", path: "/es/san-jose-south-bay" },
      { name: "Silicon Valley", path: "/es/silicon-valley" },
      { name: "Palo Alto", path: "/es/palo-alto" },
      { name: "Mountain View", path: "/es/mountain-view" },
      { name: "Sunnyvale", path: "/es/sunnyvale" },
      { name: "Cupertino", path: "/es/cupertino" },
      { name: "Santa Clara", path: "/es/santa-clara" },
      { name: "Los Gatos", path: "/es/los-gatos" },
      { name: "Gilroy", path: "/es/gilroy" },
    ],
  },
  {
    name: "East Bay (Alameda)",
    regionSlug: "alameda",
    hubPath: "/es/alameda",
    color: "from-cyan-500/20 to-blue-500/10",
    cities: [
      { name: "Alameda County (Hub)", path: "/es/alameda" },
      { name: "East Bay", path: "/es/east-bay" },
      { name: "Oakland", path: "/es/oakland" },
      { name: "Fremont", path: "/es/fremont" },
      { name: "Hayward", path: "/es/hayward" },
      { name: "Berkeley", path: "/es/berkeley" },
      { name: "Pleasantón", path: "/es/pleasanton" },
      { name: "Dublín", path: "/es/dublin" },
      { name: "Livermore", path: "/es/livermore" },
      { name: "Tri-Valley", path: "/es/tri-valley" },
      { name: "San Ramón", path: "/es/san-ramon" },
    ],
  },
  {
    name: "Contra Costa",
    regionSlug: "contra-costa",
    hubPath: "/es/contra-costa",
    color: "from-indigo-500/20 to-violet-500/10",
    cities: [
      { name: "Contra Costa (Hub)", path: "/es/contra-costa" },
      { name: "Walnut Creek", path: "/es/walnut-creek" },
      { name: "Concord", path: "/es/concord" },
      { name: "Richmond", path: "/es/richmond" },
      { name: "Antioch", path: "/es/antioch" },
    ],
  },
  {
    name: "Condado de Solano",
    regionSlug: "solano",
    hubPath: "/es/solano",
    color: "from-emerald-500/20 to-green-500/10",
    cities: [
      { name: "Solano (Hub)", path: "/es/solano" },
      { name: "Vallejo", path: "/es/vallejo" },
      { name: "Fairfield", path: "/es/fairfield" },
      { name: "Vacaville", path: "/es/vacaville" },
      { name: "Benicia", path: "/es/benicia" },
    ],
  },
  {
    name: "Condado de Marin",
    regionSlug: "marin",
    hubPath: "/es/marin",
    color: "from-rose-500/20 to-red-500/10",
    cities: [
      { name: "Marin (Hub)", path: "/es/marin" },
      { name: "San Rafael", path: "/es/san-rafael" },
      { name: "Novato", path: "/es/novato" },
      { name: "Mill Valley", path: "/es/mill-valley" },
      { name: "Sausalito", path: "/es/sausalito" },
    ],
  },
  {
    name: "Condado de Orange",
    regionSlug: "orange-county",
    hubPath: "/es/orange-county",
    color: "from-amber-500/20 to-orange-500/10",
    cities: [
      { name: "Orange County (Hub)", path: "/es/orange-county" },
      { name: "Irvine", path: "/es/irvine" },
      { name: "Anaheim", path: "/es/anaheim" },
      { name: "Santa Ana", path: "/es/santa-ana" },
      { name: "Huntington Beach", path: "/es/huntington-beach" },
      { name: "Newport Beach", path: "/es/newport-beach" },
    ],
  },
  {
    name: "Área de San Diego",
    regionSlug: "san-diego",
    hubPath: "/es/san-diego",
    color: "from-sky-500/20 to-cyan-500/10",
    cities: [
      { name: "San Diego", path: "/es/san-diego" },
      { name: "Carlsbad", path: "/es/carlsbad" },
      { name: "Chula Vista", path: "/es/chula-vista" },
      { name: "Encinitas", path: "/es/encinitas" },
      { name: "Oceanside", path: "/es/oceanside" },
    ],
  },
];

const SpanishLocationsPage = () => {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const totalCities = SPANISH_REGIONS.reduce((sum, r) => sum + r.cities.length, 0);

  return (
    <>
      <SEO
        title="Áreas de Servicio en Español — Limpieza del Hogar en California"
        description="Servicio profesional de limpieza del hogar en toda California. Encuentra tu ciudad y obtén un precio exacto en segundos. Sacramento, Los Ángeles, San Francisco, San Diego y más."
        keywords="limpieza del hogar california, servicio de limpieza sacramento, limpieza los angeles, limpieza san francisco, maid service california en español, uplevel cleaning áreas de servicio"
        url="https://uplevelcleaning.com/es/locations"
        alternateUrl="https://uplevelcleaning.com/locations"
        alternateLang="en-US"
        locationName="California"
        lang="es"
        includeLocalBusiness={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Todas las Áreas", url: "/es/locations" },
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">

          {/* Hero */}
          <section className="relative py-24 lg:py-32 bg-gradient-to-br from-primary/10 via-background to-background overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/3 blur-2xl pointer-events-none" />
            <div ref={heroRef} className={cn("container relative z-10 text-center anim-fade-up", heroVisible && "is-visible")}>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary font-bold text-sm px-4 py-2 rounded-full mb-6">
                <MapPin className="h-4 w-4" />
                Todas nuestras áreas de servicio
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 text-foreground leading-[1.1]">
                Limpieza Profesional en<br />
                <span className="text-primary">Toda California</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Servimos a más de <strong>{totalCities}+ ciudades</strong> en 10 regiones de California. Selecciona tu ciudad para ver precios exactos, disponibilidad y reservar en menos de 60 segundos.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="btn-pulse h-14 px-10 rounded-2xl bg-primary text-primary-foreground hover:bg-primary-hover text-lg font-bold shadow-xl group">
                  <a href={BOOKING_URL}>
                    Reservar Ahora
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="outline" className="h-14 px-10 rounded-2xl border-2 border-primary text-primary hover:bg-primary/10 hover:text-primary text-lg font-bold">
                  <a href={BOOKING_URL}>Ver Precio Exacto</a>
                </Button>
              </div>
              {/* Trust row */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                <span>✓ Sin cargos ocultos</span>
                <span>✓ Garantía 200%</span>
                <span>✓ Asegurados y bonificados</span>
              </div>
            </div>
          </section>

          {/* Region grid */}
          <section className="py-20 bg-muted/30">
            <div className="container">
              <div ref={gridRef} className={cn("text-center mb-14 anim-fade-up", gridVisible && "is-visible")}>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Encuentra Tu Ciudad</h2>
                <p className="text-muted-foreground max-w-xl mx-auto">Haz clic en tu ciudad para ver precios exactos y disponibilidad en tu área.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {SPANISH_REGIONS.map((region, rIdx) => (
                  <div
                    key={region.regionSlug}
                    className={cn(
                      "rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 anim-fade-up",
                      gridVisible && "is-visible",
                      `delay-${Math.min((rIdx + 1) * 100, 700)}`
                    )}
                  >
                    {/* Region header */}
                    <div className={`bg-gradient-to-r ${region.color} p-5 border-b border-border`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-primary shrink-0" />
                          <h3 className="font-bold text-lg text-foreground">{region.name}</h3>
                        </div>
                        <Link
                          to={region.hubPath}
                          className="text-xs font-bold text-primary flex items-center gap-1 hover:underline shrink-0"
                        >
                          Ver hub <ChevronRight className="h-3 w-3" />
                        </Link>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{region.cities.length} ciudades disponibles</p>
                    </div>

                    {/* City list */}
                    <div className="p-5">
                      <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {region.cities.map((city) => (
                          <li key={city.path}>
                            <Link
                              to={city.path}
                              className="flex items-center gap-1.5 text-sm text-foreground hover:text-primary transition-colors group py-0.5"
                            >
                              <ChevronRight className="h-3 w-3 text-primary/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                              {city.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-5 pt-4 border-t border-border">
                        <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary-hover font-semibold group" size="sm">
                          <a href={BOOKING_URL}>
                            Reservar en {region.name}
                            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section ref={ctaRef} className={cn("py-24 bg-primary text-primary-foreground relative overflow-hidden anim-scale-up", ctaVisible && "is-visible")}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
            <div className="container relative z-10 text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">¿No ves tu ciudad?</h2>
              <p className="text-xl opacity-90 mb-8 max-w-xl mx-auto">
                Llámanos al <a href="tel:9162995588" className="font-bold underline hover:opacity-80">(916) 299-5588</a> o envíanos un mensaje. Es posible que también podamos servirte.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-background text-primary hover:bg-background/90 font-bold text-lg px-10 py-7 h-auto rounded-2xl shadow-xl group">
                  <a href={BOOKING_URL}>
                    Obtener Precio Ahora
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="ghost" className="border-2 border-white text-white hover:bg-white/10 hover:text-white font-bold text-lg px-10 py-7 h-auto rounded-2xl">
                  <a href="tel:9162995588">Llamar Ahora</a>
                </Button>
              </div>
            </div>
          </section>

        </main>
        <Footer />
      </div>
    </>
  );
};

export default SpanishLocationsPage;
